import styled from '@emotion/styled'
import { faBook, faDumbbell, faHomeLg, faKey, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import Breakpoint from '../../../Breakpoint'
import { useMenu } from '../../../context/MenuContext'
import { useClickOutside } from '../../../hooks/useClickOutside'
import { useLangSelected } from '../../../hooks/useLangSelected'
import AdminContainer from '../../admin/AdminContainer'
import { ExternalLink } from '../ExternalLink'
import MainLink from '../MainLink'

interface SidebarProps {
    children: ReactNode
    isOpen?: boolean
}

export const CloseButton = styled.button`
    position: absolute;
    top: 20px;
    right: 20px;
    background: none;
    border: none;
    color: var(--primary-color);
    font-size: 1.5rem;
    cursor: pointer;
    z-index: 1001;
    padding: 5px;
    display: none;

    & svg {
        width: 3rem;
        height: 3rem;
    }

    @media (max-width: ${Breakpoint.TABLET_L}) {
        display: block;
    }
`

export const MemberSideBar = ({ children, isOpen = false }: SidebarProps) => {
    const { closeMenu } = useMenu()
    const sidebarRef = useRef<HTMLDivElement>(null)

    useClickOutside(sidebarRef, closeMenu)
    const location = useLocation()

    let lng = useLangSelected()

    const { t } = useTranslation()

    return (
        <>
            <SideBar ref={sidebarRef} isOpen={isOpen}>
                <CloseButton onClick={closeMenu}>
                    <FontAwesomeIcon icon={faTimes} />
                </CloseButton>
                <SidebarLink
                    to={`${lng}/bva-membre`}
                    className={location.pathname === `/${lng}/bva-membre` ? 'active' : ''}
                >
                    <FontAwesomeIcon icon={faHomeLg} /> {t('sidebar.admin.dashboard')}
                </SidebarLink>
                <SidebarLink
                    to={`/${lng}/bva-membre/documentation`}
                    className={location.pathname === `/${lng}/bva-membre/documentation` ? 'active' : ''}
                >
                    <FontAwesomeIcon icon={faBook} /> {t('sidebar.admin.documentation')}
                </SidebarLink>
                <SidebarLink
                    to={`/${lng}/bva-membre/ma-progression`}
                    className={location.pathname === `/${lng}/bva-membre/ma-progression` ? 'active' : ''}
                >
                    <FontAwesomeIcon icon={faDumbbell} /> {t('sidebar.member.my-progression')}
                </SidebarLink>
                <SidebarLink
                    to={`/${lng}/bva-membre/modifier-mon-mot-de-passe`}
                    className={location.pathname === `/${lng}/bva-membre/modifier-mon-mot-de-passe` ? 'active' : ''}
                >
                    <FontAwesomeIcon icon={faKey} /> {t('sidebar.member.password')}
                </SidebarLink>
            </SideBar>
            <AdminContainer>{children}</AdminContainer>
        </>
    )
}

export const SidebarLinkExternal = styled(ExternalLink)`
    display: flex;
    align-items: center;

    &.active {
        border: none;
    }

    & * {
        margin-right: 8px;
        padding-left: 16px;

        @media (max-width: ${Breakpoint.PHONE}) {
            height: 3rem;
        }
    }

    &:active {
        transform: scale(0.95);
    }

    @media (max-width: ${Breakpoint.TABLET_L}) {
        justify-content: flex-start;
    }
`

export const SidebarLink = styled(MainLink)`
    display: flex;
    align-items: center;
    padding: 8px 16px;

    &.active {
        border: none;
    }

    & > svg {
        margin-right: 8px;
        width: 16px;
        text-align: center;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        height: 3rem;
    }

    &:active {
        transform: scale(0.95);
    }

    @media (max-width: ${Breakpoint.TABLET_L}) {
        justify-content: flex-start;
    }
`

export const SideBar = styled.div<{ isOpen: boolean }>`
    width: 15%;
    min-width: 200px;
    min-height: calc(100vh - 166px);
    background-color: white;
    padding: 20px 0 30px 8px;
    position: fixed;
    left: 0;
    top: 130px;
    z-index: 5;
    transition: all 0.3s ease-in-out;
    overflow-y: auto;
    height: calc(100vh - 130px);
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);

    @media (max-width: ${Breakpoint.TABLET_L}) {
        transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(-100%)')};
        position: fixed;
        top: 0;
        padding-top: 70px;
        height: 100vh;
        width: 100vw;
        z-index: 1000;
    }
`

export default MemberSideBar
